import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../apiBase';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import './sms-management.css';

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });
}

const fmt = (n, cur = '₽') => `${Number(n ?? 0).toLocaleString('ru-RU', { maximumFractionDigits: 4 })} ${cur === 'RUB' ? '₽' : cur}`;

export default function SmsManagementPage() {
    const { t } = useTranslation(['adminSms', 'common']);
    const [tab, setTab] = useState('providers');
    const [providers, setProviders] = useState([]);
    const [tariffs, setTariffs] = useState([]);
    const [balances, setBalances] = useState([]);
    const [revenue, setRevenue] = useState(null);
    const [msg, setMsg] = useState('');
    const [testPhone, setTestPhone] = useState('');
    const [adjustSchoolId, setAdjustSchoolId] = useState('');
    const [adjustAmount, setAdjustAmount] = useState('');
    const [adjustReason, setAdjustReason] = useState('');

    useEffect(() => { loadTab(tab); }, [tab]);

    async function loadTab(name) {
        setMsg('');
        try {
            if (name === 'providers') {
                const res = await adminApi().get('/admin/sms/providers');
                setProviders(res.data || []);
            }
            if (name === 'tariffs') {
                const res = await adminApi().get('/admin/sms/tariffs');
                setTariffs(res.data || []);
            }
            if (name === 'balances') {
                const res = await adminApi().get('/admin/sms/schools-balance');
                setBalances(res.data || []);
            }
            if (name === 'revenue') {
                const res = await adminApi().get('/admin/sms/revenue');
                setRevenue(res.data);
            }
        } catch (e) {
            setMsg(e.response?.data?.message || t('common:error'));
        }
    }

    async function testProvider(id) {
        if (!testPhone.trim()) return;
        try {
            const res = await adminApi().post(`/admin/sms/providers/${id}/test`, { testPhone });
            setMsg(res.data?.success ? 'OK' : (res.data?.error || t('common:error')));
        } catch (e) {
            setMsg(e.response?.data?.error || t('common:error'));
        }
    }

    async function saveTariff(region, draft) {
        try {
            await adminApi().put(`/admin/sms/tariffs/${region}`, draft);
            setMsg(t('common:done'));
            loadTab('tariffs');
        } catch (e) {
            setMsg(e.response?.data?.message || t('common:error'));
        }
    }

    async function adjustBalance() {
        const schoolId = Number(adjustSchoolId);
        const smsAmount = Number(adjustAmount);
        if (!schoolId || !smsAmount) return;
        try {
            await adminApi().post(`/admin/sms/schools/${schoolId}/adjust`, {
                smsAmount,
                reason: adjustReason || t('adminSms:adjustBalance')
            });
            setMsg(t('common:done'));
            loadTab('balances');
        } catch (e) {
            setMsg(e.response?.data?.message || t('common:error'));
        }
    }

    const tabs = [
        { id: 'providers', label: t('adminSms:providers') },
        { id: 'tariffs', label: t('adminSms:tariffs') },
        { id: 'balances', label: t('adminSms:schoolsBalance') },
        { id: 'revenue', label: t('adminSms:revenue') }
    ];

    return (
        <OwnerPageLayout title={t('adminSms:title')} maxWidth={1200}>
            <div className="sms-mgmt-tabs">
                {tabs.map((x) => (
                    <button
                        key={x.id}
                        type="button"
                        className={tab === x.id ? 'active' : ''}
                        onClick={() => setTab(x.id)}
                    >
                        {x.label}
                    </button>
                ))}
            </div>
            {msg ? <p className="sms-mgmt-msg">{msg}</p> : null}

            {tab === 'providers' && (
                <div className="sms-mgmt-panel">
                    <p className="sms-mgmt-hint">{t('adminSms:addProvider')} — через API POST /admin/sms/providers</p>
                    <input
                        className="sms-mgmt-input"
                        placeholder="+7..."
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                    />
                    <table className="sms-mgmt-table">
                        <thead>
                            <tr>
                                <th>{t('adminSms:providers')}</th>
                                <th>Code</th>
                                <th>Priority</th>
                                <th>{t('adminSms:costPrice')}</th>
                                <th>{t('adminSms:testProvider')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {providers.map((p) => (
                                <tr key={p.id}>
                                    <td>{p.region} · {p.providerName} {p.isActive ? '✅' : '—'}</td>
                                    <td>{p.providerCode}</td>
                                    <td>{p.priority}{p.isDefault ? ' ★' : ''}</td>
                                    <td>{fmt(p.costPerSmsRub, 'RUB')}</td>
                                    <td>
                                        <button type="button" className="sp-btn sp-btn--ghost" onClick={() => testProvider(p.id)}>
                                            📤
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'tariffs' && (
                <div className="sms-mgmt-panel">
                    <table className="sms-mgmt-table">
                        <thead>
                            <tr>
                                <th>Region</th>
                                <th>{t('adminSms:sellPrice')}</th>
                                <th>{t('adminSms:markupPercent')}</th>
                                <th>100</th>
                                <th>500</th>
                                <th>1000</th>
                                <th>5000</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tariffs.map((tar) => (
                                <tr key={tar.id}>
                                    <td>{tar.region}</td>
                                    <td>{fmt(tar.pricePerSms, tar.currency)}</td>
                                    <td>{tar.markupPercent}%</td>
                                    <td>{fmt(tar.package100Price, tar.currency)}</td>
                                    <td>{fmt(tar.package500Price, tar.currency)}</td>
                                    <td>{fmt(tar.package1000Price, tar.currency)}</td>
                                    <td>{fmt(tar.package5000Price, tar.currency)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {tariffs[0] && (
                        <button
                            type="button"
                            className="sp-btn sp-btn--primary"
                            style={{ marginTop: 12 }}
                            onClick={() => saveTariff(tariffs[0].region, {
                                ...tariffs[0],
                                costPerSms: 0.4,
                                updatedAt: undefined,
                                id: undefined
                            })}
                        >
                            {t('common:save')}
                        </button>
                    )}
                </div>
            )}

            {tab === 'balances' && (
                <div className="sms-mgmt-panel">
                    <div className="sms-mgmt-adjust">
                        <input className="sms-mgmt-input" placeholder="schoolId" value={adjustSchoolId} onChange={(e) => setAdjustSchoolId(e.target.value)} />
                        <input className="sms-mgmt-input" placeholder="SMS ±" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} />
                        <input className="sms-mgmt-input" placeholder={t('adminSms:adjustBalance')} value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
                        <button type="button" className="sp-btn sp-btn--primary" onClick={adjustBalance}>+</button>
                    </div>
                    <table className="sms-mgmt-table">
                        <thead>
                            <tr>
                                <th>Школа</th>
                                <th>SMS</th>
                                <th>Куплено</th>
                                <th>Использовано</th>
                            </tr>
                        </thead>
                        <tbody>
                            {balances.map((b) => (
                                <tr key={b.schoolId} className={b.smsBalance <= 10 ? 'low' : ''}>
                                    <td>{b.schoolName}</td>
                                    <td>{b.smsBalance}{b.isBlocked ? ' 🔴' : ''}</td>
                                    <td>{b.totalPurchased}</td>
                                    <td>{b.totalUsed}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'revenue' && revenue && (
                <div className="sms-mgmt-panel">
                    <table className="sms-mgmt-table">
                        <thead>
                            <tr>
                                <th>Месяц</th>
                                <th>SMS</th>
                                <th>{t('adminSms:totalProfit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(revenue.usage || []).map((r) => (
                                <tr key={`${r.year}-${r.month}`}>
                                    <td>{r.month}.{r.year}</td>
                                    <td>{r.totalSms}</td>
                                    <td>{fmt(r.revenue, 'RUB')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </OwnerPageLayout>
    );
}
